import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { createUserScopedCache } from '../lib/userScopedCache';

export type FeatureFlagKey =
  | 'duels'
  | 'community'
  | 'hallOfFame'
  | 'challenge'
  | 'battlePass'
  | 'leaderboards'
  | 'threeLoopPipeline'
  | 'chironMemory';

export type FeatureFlags = Record<FeatureFlagKey, boolean>;

export const DEFAULT_FLAGS: FeatureFlags = {
  duels: false,
  community: false,
  hallOfFame: false,
  challenge: false,
  battlePass: false,
  leaderboards: false,
  threeLoopPipeline: false,
  chironMemory: false,
};

// Shared session cache — one fetch per user, auto-invalidated on auth change.
// See src/lib/userScopedCache.ts for the pattern (factored out in Wave 2a).
const flagsCache = createUserScopedCache<FeatureFlags>(
  async (userId) => {
    const { data } = await supabase
      .from('profiles')
      .select('feature_flags')
      .eq('id', userId)
      .maybeSingle();
    const raw = (data?.feature_flags ?? {}) as Partial<FeatureFlags>;
    return { ...DEFAULT_FLAGS, ...raw };
  },
  DEFAULT_FLAGS,
);

export function useFeatureFlags(): { flags: FeatureFlags; loading: boolean } {
  const [flags, setFlags] = useState<FeatureFlags>(
    flagsCache._inspect().cached ?? DEFAULT_FLAGS,
  );
  const [loading, setLoading] = useState(flagsCache._inspect().cached === null);

  useEffect(() => {
    let alive = true;
    flagsCache.get().then((loaded) => {
      if (!alive) return;
      setFlags(loaded);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  return { flags, loading };
}

export function useFeatureFlag(key: FeatureFlagKey): boolean {
  const { flags } = useFeatureFlags();
  return flags[key];
}

/** Manual invalidation (rare — auth events already clear this automatically). */
export function clearFeatureFlagCache() {
  flagsCache.clear();
}
